import { Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { formatDate } from '../../lib/intl.js';
import { formatMoney, setCurrencyDecimals } from '../../lib/money.js';
import { assetUrl, getPublicOrder, type PublicOrder } from '../../lib/publicOrder.js';

// Self-contained bilingual labels — this page is opened from a Telegram alert or
// a scanned QR, by someone with no session, so it renders outside the app shell
// and cannot lean on the loaded namespaces.
const LABELS = {
  en: {
    order: 'Order', date: 'Date', status: 'Status', subtotal: 'Subtotal', discount: 'Discount',
    tax: 'Tax', total: 'Total', cashback: 'Cashback used', due: 'Amount due', print: 'Print',
    qty: 'Qty', price: 'Price', amount: 'Amount', item: 'Description', payment: 'Payment',
    coupon: 'Coupon', notFound: 'This order link is invalid or the order was cancelled.',
    submitted: 'Submitted', fulfilled: 'Fulfilled',
    cod: 'Cash on delivery', bank_transfer: 'Bank transfer', pay_later: 'Pay later', fib: 'FIB',
    fibPaid: 'Paid via FIB',
  },
  ar: {
    order: 'طلب', date: 'التاريخ', status: 'الحالة', subtotal: 'المجموع الفرعي', discount: 'الخصم',
    tax: 'الضريبة', total: 'الإجمالي', cashback: 'المستخدم من الرصيد', due: 'المبلغ المستحق', print: 'طباعة',
    qty: 'الكمية', price: 'السعر', amount: 'المبلغ', item: 'الوصف', payment: 'الدفع',
    coupon: 'كوبون', notFound: 'رابط الطلب غير صالح أو تم إلغاء الطلب.',
    submitted: 'تم الاستلام', fulfilled: 'تم التجهيز',
    cod: 'الدفع عند الاستلام', bank_transfer: 'تحويل بنكي', pay_later: 'الدفع لاحقاً', fib: 'FIB',
    fibPaid: 'مدفوع عبر FIB',
  },
};

export function PublicOrderPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const L = LABELS[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) { setError(true); return; }
    void getPublicOrder(token)
      .then((d) => {
        // No app shell here, so register the backend's decimals before the first
        // format call — otherwise IQD renders as "8,000.00" instead of "8,000".
        if (d.currencyDecimals) setCurrencyDecimals(d.currencyDecimals);
        setOrder(d);
      })
      .catch(() => setError(true));
  }, [token]);

  const money = (a: string): string => formatMoney(a, { locale: i18n.language, currency: order?.currencyCode ?? 'USD' });
  // Quantities arrive as 4dp decimal strings ("2.0000"). A shopper reading their
  // own order should see "2" — but a genuine 1.5 kg must survive, so trim only
  // trailing zeros rather than rounding to an integer.
  const qty = (q: string): string => (q.includes('.') ? q.replace(/0+$/, '').replace(/\.$/, '') : q);
  // Unknown keys fall through to the raw value rather than rendering blank: a new
  // checkout method shipping before this map is updated must not erase the line.
  const label = (k: string): string => (L as Record<string, string>)[k] ?? k;

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 p-6 text-center">
        <p className="text-slate-500">{L.notFound}</p>
      </div>
    );
  }
  if (!order) {
    return <div className="grid min-h-screen place-items-center bg-slate-100"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" /></div>;
  }

  const showDiscount = Number(order.discountTotal) > 0;
  const cashback = Number(order.cashbackRedeemed);

  return (
    <div dir={dir} className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 flex justify-end print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            <Printer className="h-4 w-4" aria-hidden />{L.print}
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm print:rounded-none print:shadow-none">
          {/* header */}
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 p-6">
            <div className="flex items-center gap-3">
              {order.seller.logoUrl
                ? <img src={assetUrl(order.seller.logoUrl) ?? ''} alt="" className="h-12 w-12 rounded-lg object-cover" referrerPolicy="no-referrer" />
                : <div className="grid h-12 w-12 place-items-center rounded-lg bg-slate-800 text-lg font-bold text-white">{order.seller.name.slice(0, 1)}</div>}
              <div>
                <div className="text-lg font-bold text-slate-900">{order.seller.name}</div>
                {order.seller.tagline && <div className="text-sm text-slate-500">{order.seller.tagline}</div>}
              </div>
            </div>
            <div className="text-end">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{L.order}</div>
              <div className="font-mono text-xl font-bold text-slate-900">{order.orderNumber}</div>
            </div>
          </div>

          {/* meta */}
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
            <div className="space-y-1 text-sm">
              <div><span className="text-slate-400">{L.date}: </span><span className="text-slate-700">{formatDate(order.date, { locale: i18n.language })}</span></div>
              {order.paymentMethod && <div><span className="text-slate-400">{L.payment}: </span><span className="text-slate-700">{label(order.paymentMethod)}</span></div>}
            </div>
            <div className="flex flex-wrap items-start gap-2 sm:justify-end">
              {/* Money confirmed by FIB itself — the receipt says so out loud. */}
              {order.fibPaid && (
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  {L.fibPaid}
                </span>
              )}
              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {label(order.status)}
              </span>
            </div>
          </div>

          {/* lines */}
          <div className="overflow-x-auto px-6">
            <table className="w-full text-start text-sm">
              <thead>
                <tr className="border-y border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 text-start font-medium">{L.item}</th>
                  <th className="py-2 text-end font-medium">{L.qty}</th>
                  <th className="py-2 text-end font-medium">{L.price}</th>
                  <th className="py-2 text-end font-medium">{L.amount}</th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {/* A delivery fee is a charge, not something that was picked off a
                        shelf — quantity and unit price on it read as noise. */}
                    <td className="py-2 text-slate-800">{l.description || '—'}</td>
                    <td className="py-2 text-end font-mono text-slate-600">{l.isCharge ? '' : qty(l.quantity)}</td>
                    <td className="py-2 text-end font-mono text-slate-600">{l.isCharge ? '' : money(l.unitPrice)}</td>
                    <td className="py-2 text-end font-mono text-slate-800">{money(l.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* totals */}
          <div className="flex justify-end p-6">
            <dl className="w-full max-w-xs space-y-1 text-sm">
              <Row label={L.subtotal} value={money(order.subtotal)} />
              {showDiscount && <Row label={order.couponCode ? `${L.discount} (${order.couponCode})` : L.discount} value={`−${money(order.discountTotal)}`} muted />}
              {Number(order.taxTotal) > 0 && <Row label={L.tax} value={money(order.taxTotal)} />}
              <div className="my-1 border-t border-slate-200" />
              <Row label={L.total} value={money(order.total)} bold />
              {/* Cashback settles the order, it does not re-price it — so it sits
                  BELOW the total, and the amount due is what is actually collected. */}
              {cashback > 0 && <Row label={L.cashback} value={`−${money(order.cashbackRedeemed)}`} muted />}
              {cashback > 0 && <Row label={L.due} value={money(order.amountDue)} bold />}
            </dl>
          </div>

          {(order.seller.email || order.seller.phone || order.seller.addressLines.length > 0) && (
            <div className="border-t border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 print:bg-white">
              {[order.seller.phone, order.seller.email, ...order.seller.addressLines].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted }: { label: string; value: string; bold?: boolean; muted?: boolean }): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <dt className={muted ? 'text-slate-400' : 'text-slate-500'}>{label}</dt>
      <dd className={`font-mono ${bold ? 'text-base font-bold text-slate-900' : muted ? 'text-slate-500' : 'text-slate-800'}`}>{value}</dd>
    </div>
  );
}
